import React, { useState, useEffect } from 'react';
import { Box, Typography, Avatar, Grid, Skeleton, useTheme } from '@mui/material';
import { Person } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { Link } from 'react-router-dom';
import { getGameUsers } from '../../services/api';

interface User {
  user_id: number;
  username: string;
  display_name?: string;
  avatar_url?: string;
  status: number;
  score?: number;
}

interface CommunitySectionProps {
  gameId: number;
  gameName?: string;
}

const UserAvatarGrid = styled(Grid)({
  display: 'flex',
  flexDirection: 'row',
  flexWrap: 'wrap',
  gap: '8px',
  marginTop: '16px'
});

const UserAvatarLink = styled(Link)({
  textDecoration: 'none',
  position: 'relative',
  borderRadius: '50%',
  '&:hover .user-avatar': {
    transform: 'scale(1.1)',
    boxShadow: '0 4px 12px rgba(255, 255, 255, 0.2)',
  },
  '&:hover .user-tooltip': {
    opacity: 1,
    visibility: 'visible',
  }
});

const UserAvatar = styled(Avatar)({
  width: 36,
  height: 36,
  transition: 'all 0.2s ease',
  border: '0px solid rgba(255, 255, 255, 0.1)',
  backgroundColor: '#424242',
  '&:hover': {
    borderColor: 'rgba(255, 255, 255, 0.3)',
  }
});

const UserTooltip = styled(Box)({
  position: 'absolute',
  bottom: '50px',
  left: '50%',
  transform: 'translateX(-50%)',
  backgroundColor: 'rgba(0, 0, 0, 0.9)',
  color: 'white',
  padding: '8px 12px',
  borderRadius: '6px',
  fontSize: '0.75rem',
  whiteSpace: 'nowrap',
  opacity: 0,
  visibility: 'hidden',
  transition: 'all 0.2s ease',
  zIndex: 10,
  backdropFilter: 'blur(8px)',
  border: '1px solid rgba(255, 255, 255, 0.1)',
  '&::after': {
    content: '""',
    position: 'absolute',
    top: '100%',
    left: '50%',
    transform: 'translateX(-50%)',
    border: '5px solid transparent',
    borderTop: '5px solid rgba(0, 0, 0, 0.9)',
  }
});

const StatusIndicator = styled(Box)<{ status: number }>(({ theme, status }) => {
  const getStatusColor = (status: number) => {
    switch (status) {
      case 1: return theme.palette.primary.main; // プレイ済み
      case 2: return theme.palette.info.main; // プレイ中
      case 3: return theme.palette.warning.main; // 中断
      case 4: return '#b99a71'; // 積みゲー
      case 5: return theme.palette.secondary.main; // ウィッシュ
      default: return '#757575';
    }
  };

  return {
    position: 'absolute',
    bottom: -2,
    right: -2,
    width: 12,
    height: 12,
    borderRadius: '50%',
    backgroundColor: getStatusColor(status),
    border: '2px solid #121212',
  };
});

const CommunitySection: React.FC<CommunitySectionProps> = ({ gameId, gameName }) => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    const fetchGameUsers = async () => {
      try {
        setLoading(true);
        const data = await getGameUsers(gameId);
        setUsers(data.users || []);
        setTotalCount(data.total_count || 0);
      } catch (error) {
        console.error('Error fetching game users:', error);
      } finally {
        setLoading(false);
      }
    };

    if (gameId) {
      fetchGameUsers();
    }
  }, [gameId]);

  const getStatusText = (status: number) => {
    switch (status) {
      case 1: return 'プレイ済み';
      case 2: return 'プレイ中';
      case 3: return '中断';
      case 4: return '積みゲー';
      case 5: return 'ウィッシュ';
      default: return '';
    }
  };

  const getScoreText = (score?: number) => {
    if (!score) return '';
    return ` ★${score}`;
  };

  if (loading) {
    return (
      <Box sx={{ mb: 3, mt: { xs: 4, sm: 4 } }}>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          mb: 1 
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Skeleton variant="text" width={80} height={32} />
            <Skeleton variant="text" width={30} height={24} />
          </Box>
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {[...Array(12)].map((_, index) => (
            <Skeleton
              key={index}
              variant="circular"
              width={40}
              height={40}
            />
          ))}
        </Box>
      </Box>
    );
  }

  if (users.length === 0) {
    return null;
  }

  return (
    <Box sx={{ mb: 3, mt: { xs: 4, sm: 4 } }}>
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        mb: 1 
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h6" component="h2" sx={{ fontSize: { xs: '0.75rem', sm: '0.8rem' } }}>
            コミュニティ
          </Typography>
          <Box
            sx={{
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              color: 'white',
              fontSize: '0.7rem',
              padding: '2px 8px',
              borderRadius: 1,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            {totalCount}
          </Box>
        </Box>
      </Box>
      
      <UserAvatarGrid container>
        {users.map((user) => (
          <UserAvatarLink 
            key={user.user_id}
            to={`/profile/${user.username || user.user_id}`}
          >
            <UserAvatar 
              className="user-avatar"
              src={user.avatar_url || undefined}
              alt={user.display_name || user.username || `User ${user.user_id}`}
            >
              {!user.avatar_url && <Person />}
            </UserAvatar>
            <StatusIndicator status={user.status} />
            <UserTooltip className="user-tooltip">
              {user.display_name || user.username || `User ${user.user_id}`}
              <br />
              {getStatusText(user.status)}{getScoreText(user.score)}
            </UserTooltip>
          </UserAvatarLink>
        ))}
      </UserAvatarGrid>
      
      {totalCount > 50 && (
        <Typography 
          variant="body2" 
          sx={{ 
            mt: 2, 
            color: 'rgba(255, 255, 255, 0.7)',
            fontSize: '0.8rem'
          }}
        >
          他{totalCount - 50}人のユーザーが記録しています
        </Typography>
      )}
    </Box>
  );
};

export default CommunitySection;